import { useCallback } from "react";
import { useTranslations } from "use-intl";
import { Hidable, instanceNameOf, isHidden, valueOrNull } from "@/shared/value";
import {
  isNameHidden,
  namePartValue,
  PersonNamePart,
} from "./person-name-part";
import { PersonNameFormat, toPersonName } from "./to-person-name";

type PersonNameParts = {
  lastName?: PersonNamePart;
  firstName?: PersonNamePart;
  middleName?: PersonNamePart;
};

/**
 * Имя строкой — для мест, куда компонент не подставить: заголовок события календаря,
 * ячейка выгружаемого отчёта, `title`. Скрытое имя не превращается в пустоту: берём имя,
 * отданное защитой, а если его нет — слово «Скрыто», иначе в предложении остаётся дыра.
 */
export function usePersonNameString() {
  const t = useTranslations("Common");

  return useCallback(
    (
      person: Hidable<PersonNameParts>,
      format: PersonNameFormat = "full",
    ): string => {
      const value = valueOrNull(person);

      if (!value) {
        return isHidden(person) ? instanceNameOf(person) || t("Hidden") : "";
      }

      const parts =
        format === "name"
          ? [value.lastName, value.firstName]
          : [value.lastName, value.firstName, value.middleName];

      if (isNameHidden(...parts)) {
        return instanceNameOf(parts.find(isHidden)) || t("Hidden");
      }

      return toPersonName(
        format,
        namePartValue(value.lastName),
        namePartValue(value.firstName),
        namePartValue(value.middleName),
      );
    },
    [t],
  );
}
